/**
 * @typedef {object} FilterOption
 * @property {string} value
 * @property {string} label
 * @property {boolean} [disabled]
 */

/**
 * @typedef {object} FilterSlotConfig
 * @property {string} id
 * @property {string} label — Placeholder / filter name (e.g. "Owner", "Type")
 * @property {boolean} [multiple=true]
 * @property {string[]} value — Controlled selected values
 * @property {(next: string[]) => void} onChange
 * @property {FilterOption[]} options
 * @property {number} [maxVisible=3] — Reserved for "+N more" collapse (follow-up)
 * @property {boolean} [disabled]
 */

/**
 * @typedef {object} FiltersI18n
 * @property {(label: string) => string} [removeFilter]
 * @property {(label: string) => string} [addedAnnounce]
 * @property {(label: string) => string} [removedAnnounce]
 * @property {string} [clearedAllAnnounce]
 * @property {string} [clearFilters]
 * @property {string} [allFilters]
 * @property {(count: number) => string} [moreChip]
 * @property {string} [sortAsc] — Aria label for the "ascending" direction button
 * @property {string} [sortDesc] — Aria label for the "descending" direction button
 */

/**
 * @typedef {'asc' | 'desc'} SortDirection
 */

/**
 * @typedef {object} FilterSortConfig
 * @property {string} [label='Name'] — Placeholder cuando no hay `value`
 * @property {string} [value] — Valor seleccionado (debe coincidir con `options[].value`)
 * @property {string} [defaultValue] — Valor tras Clear Filters; default: primera opción
 * @property {FilterOption[]} options — Campos de orden: `{ value, label }[]` → {@link MenuPanel}
 * @property {(next: string) => void} [onChange] — Al elegir una opción del menú
 * @property {SortDirection} [direction='desc']
 * @property {SortDirection} [defaultDirection='desc'] — Dirección tras Clear Filters
 * @property {(next: SortDirection) => void} [onDirectionChange]
 * @property {boolean} [disabled]
 */

/**
 * @typedef {object} ViewToggleOption
 * @property {string} value
 * @property {import('lucide-react').LucideIcon} icon
 * @property {string} label — Aria label
 */

/**
 * @typedef {object} ViewToggleConfig
 * @property {string} value
 * @property {(next: string) => void} onChange
 * @property {ViewToggleOption[]} [options] — Default: list + grid; Figma 7996:5370 uses grid + table
 * @property {boolean} [hidden=false] — If true, ViewToggle is not rendered
 * @property {string} [ariaLabel] — Aria label of the toggle group
 */

/**
 * @typedef {object} FiltersProps
 * @property {import('react').ComponentProps<typeof import('../SearchInput.jsx').default>} [searchProps]
 * @property {FilterSlotConfig[]} filters — Todos los slots; visibilidad en barra según viewport + `maxFilters` (resto en slideout «All filters»)
 * @property {number} [maxFilters=4] — Tope de slots visibles en barra en desktop (0–4). El límite efectivo es `min(maxFilters, responsive)` — ver `filtersResponsive.js`
 * @property {number} [maxVisibleFilters] — Alias legacy de `maxFilters`; usar `maxFilters` en código nuevo.
 * @property {() => void} [onClearAll] — Optional hook after all filter slots are cleared
 * @property {import('react').ReactNode} [actions] — Free trailing slot (rendered in the right group)
 * @property {FilterSortConfig} [sort] — Sort dropdown + direction toggle (Figma 7996:5370)
 * @property {ViewToggleConfig} [viewToggle] — Grid/list toggle; `hidden: true` to skip
 * @property {boolean} [showAllFilters=true] — Muestra u oculta el botón pill «All filters». `false` lo oculta siempre.
 * @property {() => void} [onAllFiltersClick] — Click en All filters
 * @property {FiltersI18n} [i18n]
 * @property {string} [className]
 * @property {string} [rowClassName]
 */

export const FILTERS_CHIP_MAX_CHARS = 14;
